import { useState, useEffect } from 'react'
import i18n from 'i18next'

/**
 * Returns true when translations for the given namespace are loaded.
 * 
 * Strategy:
 * 1. If translations already loaded → ready immediately
 * 2. If i18n not yet initialized → wait for 'initialized' then check
 * 3. If i18n initialized but bundle empty → wait for 'added' event
 * 4. If translations fail to load → i18next uses fallbackLng, we proceed anyway
 * 
 * No artificial timeout needed - i18next handles retries internally.
 */
export function useTranslationReady(namespace: string): boolean {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    setReady(false)

    const checkBundle = () => {
      // hasResourceBundle returns true if namespace has any resources loaded
      const bundle = i18n.getResourceBundle(i18n.language, namespace)
      if (bundle && Object.keys(bundle).length > 0) {
        setReady(true)
        return true
      }
      return false
    }

    // Fast path: translations already loaded
    if (checkBundle()) {
      return
    }

    // Handle i18next not yet initialized
    if (!i18n.isInitialized) {
      const handleInit = () => {
        if (checkBundle()) {
          setReady(true)
        }
      }
      i18n.on('initialized', handleInit)
      return () => i18n.off('initialized', handleInit)
    }

    // i18n initialized but bundle not yet loaded - wait for 'added' event
    const handleAdded = () => {
      checkBundle() && setReady(true)
    }

    i18n.store.on('added', handleAdded)
    return () => i18n.store.off('added', handleAdded)
  }, [namespace])

  return ready
}
